import {
  ActionItemStatus, ProviderActivationEvent, ProviderActivationEventName
} from '@uplift/api';

import { OnboardingSteps } from './types';

export type ProviderOnboardingToDoStatus = (
  | 'credentialing'
  | 'matchingProfile'
  | 'matchingAvailability'
  | 'stateSelection'
  | 'mfaAdded'
  | 'payoutAccountConfigured'
);

export const hasEvent = (events: ProviderActivationEvent[], eventName: ProviderActivationEventName) => {
  return events?.some((event) => event.eventName === eventName);
};

export const getActiveStep = (events: ProviderActivationEvent[]): OnboardingSteps => {
  if (hasEvent(events, ProviderActivationEventName.confettiDisplayed) || !hasEvent(events, ProviderActivationEventName.onboardingInitial)) {
    return null;
  }

  if (!hasEvent(events, ProviderActivationEventName.introductionVideoCompleted)) {
    return OnboardingSteps.Welcome;
  }

  if (hasEvent(events, ProviderActivationEventName.live)) {
    return OnboardingSteps.Live;
  }

  const statuses = eventsToStatus(events);
  const isTodo = Object.values(statuses).some((status) => status === ActionItemStatus.todo);

  if (isTodo) {
    return OnboardingSteps.ToDo;
  }
  const isApproved = Object.values(statuses).every((status) => status === ActionItemStatus.approved);

  if (isApproved) {
    return OnboardingSteps.Approved;
  }

  return OnboardingSteps.InReview;
};

export const eventsToStatus = (events: ProviderActivationEvent[]) => {
  const eventsToStatusMap = {
    credentialing_submitted: { entity: 'credentialing', status: ActionItemStatus.inProgress },
    credentialing_approved: { entity: 'credentialing', status: ActionItemStatus.approved },
    matching_profile_submitted: { entity: 'matchingProfile', status: ActionItemStatus.inReview },
    matching_profile_approved: { entity: 'matchingProfile', status: ActionItemStatus.approved },
    matching_availability_submitted: { entity: 'matchingAvailability', status: ActionItemStatus.inReview },
    matching_availability_approved: { entity: 'matchingAvailability', status: ActionItemStatus.approved },
    state_selection_submitted: { entity: 'stateSelection', status: ActionItemStatus.inReview },
    state_selection_approved: { entity: 'stateSelection', status: ActionItemStatus.approved },
    mfa_added: { entity: 'mfaAdded', status: ActionItemStatus.done },
  };

  const statuses: Partial<Record<ProviderOnboardingToDoStatus, ActionItemStatus>> = {
    credentialing: ActionItemStatus.inProgress,
    matchingProfile: ActionItemStatus.todo,
    matchingAvailability: ActionItemStatus.todo,
    stateSelection: ActionItemStatus.todo,
    mfaAdded: ActionItemStatus.todo,
  };

  if (events.find(e => e.eventName === ProviderActivationEventName.stripeConnectInProgress)) {
    statuses.payoutAccountConfigured = ActionItemStatus.inProgress;
  } else if (events.find(e => e.eventName === ProviderActivationEventName.stripeConnectCompleted)) {
    statuses.payoutAccountConfigured = ActionItemStatus.done;
  } else {
    statuses.payoutAccountConfigured = ActionItemStatus.todo;
  }

  events
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .forEach((event) => {
      const status = eventsToStatusMap[event.eventName];

      if (status) {
        statuses[status.entity] = status.status;
      }
    });

  return statuses;
};

export const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

  if (newWindow) newWindow.opener = null;
};

export const getChipColor = (status: ActionItemStatus) => {
  switch (status) {
    case ActionItemStatus.inReview:
    case ActionItemStatus.inProgress:
      return 'info';
    case ActionItemStatus.approved:
    case ActionItemStatus.done:
      return 'successLight';
    default:
      return 'default';
  }
};
